import { Button, NonIdealState } from "@blueprintjs/core";
import { IconNames } from "@blueprintjs/icons";
import { FormikProps } from "formik";
import moment from "moment";
import {
  ButtonContainer,
  FGCheckboxInput,
  FGCustomInput,
  FGCustomPanel,
  FGDateInput,
  FGListen,
  FGSelectInput,
  FGTextAreaInput,
  FGTextInput,
  FieldGroup,
  FieldSet,
  IFGContext,
  showError,
  showSuccess,
} from "nsitools-react";
import * as React from "react";
import { Link } from "react-router-dom";
import * as Yup from "yup";

import {
  BroodRequestApi,
  BroodRequestDto,
  CountryApi,
  EDogSex,
  ERequestState,
  PersonApi,
  ReferentialApi,
  RequestApi,
  RequestDto,
  RequestInfoDto,
} from "../../../../api";
import {
  FGCountrySelect,
  FGDogSearchInput,
  FGPeopleSearchInput,
  FGSRSHCheckboxInput,
  PrintButton,
  SharepointButton,
  SmallFormGenerator,
} from "../../../../components";
import { useDialog, useEventsContext, useGlobalData } from "../../../../contexts";
import { ERoutes } from "../../../../ERoutes";
import { useApiService, useApplication, useCrudApi, useWindow } from "../../../../hooks";
import { useReferential } from "../../../../hooks/useReferential";
import { ETLCodes } from "../../../../locales";
import { BroodRequestPotentialFatherSelector } from "./BroodRequestPotentialFatherSelector";
import { useQuery } from "react-query";

export interface IBroodRequestDetailProps {
  broodRequestId: number;
  editMode: boolean;
  requestInfo: RequestInfoDto;
}

export const BroodRequestDetail: React.FunctionComponent<IBroodRequestDetailProps> = ({
  broodRequestId,
  editMode,
  requestInfo,
}) => {
  const api = useApiService(BroodRequestApi);
  const requestApi = useApiService(RequestApi);
  const referentialApi = useApiService(ReferentialApi);
  const personApi = useApiService(PersonApi);
  const countryApi = useApiService(CountryApi);
  const formikRef = React.useRef<FormikProps<BroodRequestDto>>();
  const [loadingReprint, setLoadingReprint] = React.useState(false);

  const { validators, t, history } = useApplication();
  /* ========== Hooks and needed variables ===========*/
  const { currentLanguage } = useGlobalData();
  const { openNewTab } = useWindow();
  const { showDialogPromise } = useDialog();

  const [loadingNewPotentialFather, setLoadingNewPotentialFather] = React.useState(false);
  const babyDogAuthorized = React.useMemo(async () => {
    return (await api.isBabyDogAuthorized(broodRequestId)).value;
  }, [api, broodRequestId]);

  /* ========== Referentials ===========*/
  const [labStatuses, lsloading] = useReferential((a) => a.getLabStatuses(), true);
  const [postalCodes, pcloading] = useReferential((a) => a.getStringifiedBelgianPostalCodes(), true, []);
  const [postalCode, setPostalCode] = React.useState("");

  /* ========== Getting Data ===========*/
  const { data, loading, saving, saveItem, refresh } = useCrudApi<BroodRequestDto>(
    React.useMemo(
      () => ({
        getApiFn: async (id = broodRequestId) => {
          if (id > 0) {
            return api.get(id);
          } else {
            const belgiumId = await countryApi.getBelgianId();
            console.log("BELGIUM ID", belgiumId);
            return new BroodRequestDto({
              countryId: belgiumId,
              request: new RequestDto({
                status: ERequestState.REQUESTED,
                remark: "",
              }),
              potentialFatherIds: [],
            }).toJSON();
          }
        },
        saveApiFn: (d: BroodRequestDto) => api.save(d),
        onSavedRoute: (d: BroodRequestDto) => `${ERoutes.broodRequest}/${d.broodRequestId}/detail/edit`,
      }),
      [broodRequestId, api, countryApi]
    ),
    [broodRequestId]
  );

  const onRequestRefreshEvent = React.useCallback(() => {
    refresh();
  }, [refresh]);

  const { subscribeToEvent, unsubscribeEvent } = useEventsContext();

  React.useEffect(() => {
    subscribeToEvent("RequestRefresh", onRequestRefreshEvent);
    return () => unsubscribeEvent("RequestRefresh", onRequestRefreshEvent);
  }, [onRequestRefreshEvent, subscribeToEvent, unsubscribeEvent]);

  const [saveChecking, setSaveChecking] = React.useState(false);
  const saveCheck = React.useCallback(
    async (values) => {
      setSaveChecking(true);
      let isBanned = false;
      if (broodRequestId <= 0) {
        isBanned = (await personApi.isPersonExcludedQuery(values?.request?.personId)).value;
      }
      if (isBanned) {
        const isbannedValidation = await showDialogPromise({
          message: t(ETLCodes.PersonIsBannedConfirmMessage),
          title: t(ETLCodes.Warning),
        });
        if (isbannedValidation !== "yes") return;
      }
      if (!values.otherAddress) {
        values.countryId = null;
      }
      setSaveChecking(false);
      await saveItem(values);
    },
    [broodRequestId, personApi, saveItem, showDialogPromise, t]
  );

  const { data: personAddress } = useQuery(["personAddress", data?.request?.personId], async () => {
    return personApi.getPersonAddress(data?.request?.personId ?? 0, currentLanguage.languageId);
  });

  /* ========== Yup ===========*/
  const RequestedFormSchema = React.useMemo(() => {
    return Yup.object().shape({
      motherId: Yup.number()
        .nullable()
        .required(t(ETLCodes.Required))
        .when("status", {
          is: ERequestState.REQUESTED,
          then: Yup.number()
            .test("dog-deceased", t(ETLCodes.DogIsDeceased), validators.dog.dogAlive)
            .test("is-not-excluded", t(ETLCodes.DogIsExcluded), validators.dog.dogNotExcluded),
        }),
      isBrcsRequest: Yup.boolean()
        .nullable()
        .test("isFlemishBrcs", t(ETLCodes.BRCSOnlyAvailableForFlemishBreeders), async function (c) {
          if (!c) {
            return true;
          }
          if (this.parent.otherAddress) {
            const isFlemishPostalCode = await personApi.isPostalCodeFlemish(this.parent.postalCode);
            return isFlemishPostalCode.value;
          }
          const isFlemish = await personApi.isPersonFlemish(this.parent.request.personEmail);
          return isFlemish.value;
        }),
      street: Yup.string()
        .nullable()
        .test("is-required", t(ETLCodes.Required), async function (value) {
          if (this.parent.otherAddress) {
            return !!value;
          }
          return true;
        }),
      number: Yup.string()
        .nullable()
        .test("is-required", t(ETLCodes.Required), async function (value) {
          if (this.parent.otherAddress) {
            return !!value;
          }
          return true;
        }),
      city: Yup.string()
        .nullable()
        .test("is-required", t(ETLCodes.Required), async function (value) {
          if (this.parent.otherAddress) {
            return !!value;
          }
          return true;
        }),
      postalCode: Yup.number()
        .nullable()
        .test("is-required", t(ETLCodes.Required), async function (value) {
          if (this.parent.otherAddress) {
            return !!value;
          }
          return true;
        }),
      countryId: Yup.number()
        .nullable()
        .test("is-not-begium", t(ETLCodes.IsNotBelgium), async function (value) {
          if (this.parent.otherAddress) {
            const result = await countryApi.isBelgian(value);
            return result.value;
          }
          return true;
        }),
      protrusionDate: Yup.date()
        .nullable()
        .required(t(ETLCodes.Required))
        .max(new Date(), t(ETLCodes.DateCannotBeFuture))
        .test(
          "requestDate-diff",
          t(ETLCodes.ThereMustBe44DaysMaximumBetweenProtrusionAndRequestDate),
          async function (value) {
            if (value) {
              const diff = moment(value).diff(this.parent.request.requestDate, "days");
              return diff <= 44;
            }
            return true;
          }
        ),
      babyDogPublication: Yup.boolean().test("babydog-authorized", t(ETLCodes.WaloonWithoutHK), async function (value) {
        if (!broodRequestId || !value) return true;
        return await babyDogAuthorized;
      }),

      request: Yup.object().shape({
        personId: Yup.number()
          .nullable()
          .required(t(ETLCodes.Required))
          .when("status", {
            is: ERequestState.REQUESTED,
            then: Yup.number()
              .test("is-not-deseased", t(ETLCodes.PersonISDeceased), validators.person.isPersonAlive)
              .test(
                "is-not-owner-of-excluded-kennel",
                t(ETLCodes.PersonIsOwnerOfExcludedKennel),
                validators.person.isNotOwnerOfExcludedKennel
              )
              .test("is-dog-owner", t(ETLCodes.IsNotDogOwner), function (value) {
                return validators.person.isDogOwner(formikRef.current.values.motherId, value);
              }),
          })
          .test("missing-phone-number", t(ETLCodes.MissingPhoneNumber), validators.person.missingPhoneNumber)
          .test(
            "missing-adress-information",
            t(ETLCodes.MissingCompleteAdressOrEmail),
            validators.person.missingAddress
          )
          .test("kennel-unique", t(ETLCodes.IsOwnerOfMoreThanOneKennel), validators.person.kennelUnique),
        requestDate: Yup.date().nullable().required(t(ETLCodes.Required)),
      }),
    });
  }, [
    t,
    validators.dog.dogAlive,
    validators.dog.dogNotExcluded,
    validators.person,
    countryApi,
    broodRequestId,
    babyDogAuthorized,
  ]);

  /* ========== Variables used for display rules ===========*/
  const editable = React.useMemo(
    () =>
      data?.request?.status === ERequestState.REQUESTED &&
      (requestInfo?.masterRequestStateCode === ERequestState.REQUESTED || requestInfo?.masterRequestStateCode === null),
    [data, requestInfo]
  );

  const blocked = React.useMemo(
    () =>
      requestInfo?.isRequestBlocked ||
      requestInfo?.isMasterRequestBlocked ||
      requestInfo?.requestStateCode === ERequestState.EXECUTED ||
      (requestInfo?.masterRequestStateCode != null &&
        !(requestInfo?.masterRequestStateCode === ERequestState.REQUESTED)),
    [requestInfo]
  );

  const onCancel = React.useCallback(() => history.push(`${ERoutes.broodRequest}`), [history]);

  const [shpLoading, setShpLoading] = React.useState(false);
  const existsInShp = React.useMemo(() => !!data?.request.sharepointUri, [data?.request]);
  const openOrCreateInShp = React.useCallback(async () => {
    setShpLoading(true);
    if (!!data?.request.sharepointUri) {
      window.open(data?.request.sharepointUri, "_blank");
      setShpLoading(false);
    } else {
      try {
        await requestApi.createInSharepoint(data?.requestId);
        refresh();
      } catch (e) {
        throw e;
      } finally {
        setShpLoading(false);
      }
    }
  }, [data?.request.sharepointUri, data?.requestId, requestApi, refresh]);

  const onPersonChanged = React.useCallback(
    async (value, formik) => {
      if (value !== data?.request?.personId) {
        const owner = await personApi.get(value);
        const isWaloon = (await personApi.isPersonWaloon(value)).value;
        const res = (isWaloon && owner.hkNumber !== null) || !isWaloon;
        formik.setFieldValue("babyDogPublication", res);
      }
    },
    [data?.request?.personId, personApi]
  );

  const [formWidth, setFormWidth] = React.useState<number>();

  const onAddNewFather = React.useCallback(
    async (formik) => {
      setLoadingNewPotentialFather(true);
      await api.addPotentialFather(broodRequestId, formik?.values?.newFatherId);
      formik.setFieldValue("newFatherId", null);
      setLoadingNewPotentialFather(false);
      refresh();
    },
    [api, broodRequestId, refresh]
  );

  const reprintExecutionLetter = React.useCallback(async () => {
    try {
      setLoadingReprint(true);
      await api.reprintExecutionLetter(broodRequestId);
      showSuccess(t(ETLCodes.Success));
    } catch {
      showError(t(ETLCodes.Error));
    }
    setLoadingReprint(false);
  }, [api, broodRequestId, t]);

  const [localities, locloading] = useReferential(
    (a) => a.getStringifiedBelgianLocalityFromPostalCode(postalCode ?? ""),
    false,
    [postalCode]
  );
  /* ========== Return ===========*/
  return (
    <SmallFormGenerator
      initialValues={data}
      editMode={editMode}
      onSubmit={saveCheck}
      validationSchema={RequestedFormSchema}
      loading={loading}
      saving={saving || saveChecking}
      onCancel={onCancel}
      hideButtons={data?.request?.status === ERequestState.EXECUTED}
      formikInnerRef={(f) => (formikRef.current = f)}
      onFormWidthChanged={setFormWidth}
    >
      <FGListen
        field="postalCode"
        onChanged={(value, formik) => {
          console.log("postalcode", value);
          setPostalCode(value);
        }}
      ></FGListen>
      <FGListen field="request.personId" onChanged={onPersonChanged} />
      <FieldSet
        title={t(ETLCodes.RequestData)}
        rightElement={
          <ButtonContainer>
            {broodRequestId > 0 && (
              <SharepointButton existsInShp={existsInShp} loading={shpLoading} onClick={openOrCreateInShp} />
            )}
            {requestInfo?.requestStateCode === ERequestState.EXECUTED && (
              <PrintButton
                loading={loadingReprint}
                text={t(ETLCodes.ReprintExecutionLetter)}
                onClick={reprintExecutionLetter}
              />
            )}
          </ButtonContainer>
        }
      >
        <FieldGroup columns={2}>
          <FGDateInput
            name="request.requestDate"
            label={t(ETLCodes.RequestDate)}
            requiredMark
            readonly={
              !!requestInfo &&
              (!editable || blocked || (requestInfo?.masterRequestId != undefined && requestInfo?.masterRequestId > 0))
            }
          />

          <FGSelectInput
            name="labStatusId"
            label={t(ETLCodes.LabStatus)}
            items={labStatuses}
            loading={lsloading}
            readonly
            visible={(ctx: IFGContext<BroodRequestDto>) => {
              return (
                ctx.formik.values.request.status === ERequestState.LAB_WORK ||
                ctx.formik.values.request.status === ERequestState.LAB_RESULTS_RECEIVED ||
                ctx.formik.values.request.status === ERequestState.EXECUTED_AWAITING_DOCUMENTS ||
                ctx.formik.values.request.status === ERequestState.EXECUTED
              );
            }}
            requiredMark
          />
        </FieldGroup>
        <FieldGroup columns={2}>
          <FGDogSearchInput
            name="motherId"
            defaultSearch={{ sex: EDogSex.Female, isForBroodRequest: true }}
            label={t(ETLCodes.Dog)}
            readonly={!editable || blocked || requestInfo?.masterRequestId > 0}
            onItemViewClick={(item) => openNewTab(`${ERoutes.dog}/${item}/detail/view`)}
            requiredMark
          />
          <FGTextAreaInput
            name="request.remark"
            label={t(ETLCodes.Remark)}
            readonly={data?.request?.status === ERequestState.EXECUTED}
          />
        </FieldGroup>
        <FieldGroup columns={2}>
          <FGPeopleSearchInput
            name="request.personId"
            label={t(ETLCodes.Contact)}
            readonly={!editable || blocked || requestInfo?.masterRequestId > 0}
            onItemViewClick={(item) => openNewTab(`${ERoutes.people}/${item}/detail/view`)}
            requiredMark
            dogId={(ctx: IFGContext<BroodRequestDto>) => {
              return ctx.formik.values.motherId;
            }}
            showWarning={true}
          />
        </FieldGroup>
        <FieldGroup columns={2}>
          <div>
            <FGCustomInput label={t(ETLCodes.Address)}>{() => <>{personAddress}</>}</FGCustomInput>
            <FGCheckboxInput
              name="progenusQuickDelivery"
              label={t(ETLCodes.ProgenusQuickDelivery)}
              readonly={
                data?.request?.status !== ERequestState.REQUESTED || blocked || requestInfo?.masterRequestId > 0
              }
            />{" "}
            <FGCheckboxInput
              name="isRegisteredLetter"
              label={t(ETLCodes.IsRegisteredLetter)}
              readonly={
                (data?.request?.status !== ERequestState.REQUESTED &&
                  data?.request?.status !== ERequestState.DOG_VALIDATION &&
                  data?.request?.status !== ERequestState.GESTATION) ||
                blocked ||
                requestInfo?.masterRequestId > 0
              }
            />
            <FGCheckboxInput name="isBrcsRequest" label={t(ETLCodes.IsBrcsRequest)} readonly />
            <FGCheckboxInput
              name="billMotherGenealogyCompletion"
              label={t(ETLCodes.BillMotherGenealogyCompletion)}
              readonly={
                (data?.request?.status !== ERequestState.REQUESTED &&
                  data?.request?.status !== ERequestState.DOG_VALIDATION &&
                  data?.request?.status !== ERequestState.GESTATION) ||
                blocked ||
                requestInfo?.masterRequestId > 0
              }
            />
            <FGCheckboxInput
              name="billFatherGenealogyCompletion"
              label={t(ETLCodes.BillFatherGenealogyCompletion)}
              readonly={
                (data?.request?.status !== ERequestState.REQUESTED &&
                  data?.request?.status !== ERequestState.DOG_VALIDATION &&
                  data?.request?.status !== ERequestState.GESTATION) ||
                blocked ||
                requestInfo?.masterRequestId > 0
              }
            />
          </div>
        </FieldGroup>
        <FieldGroup columns={2}>
          <FGSRSHCheckboxInput
            label={t(ETLCodes.OtherAddress)}
            name="otherAddress"
            disabled={requestInfo?.requestStateCode !== ERequestState.REQUESTED}
          />
        </FieldGroup>
        <FieldGroup columns={3}>
          <FGTextInput
            label={t(ETLCodes.Street)}
            name="street"
            readonly={!editable || blocked || requestInfo?.masterRequestId > 0}
            visible={(ctx: IFGContext<BroodRequestDto>) => {
              return ctx.formik.values.otherAddress;
            }}
            requiredMark
          />
          <FGTextInput
            label={t(ETLCodes.Number)}
            name="number"
            readonly={!editable || blocked || requestInfo?.masterRequestId > 0}
            visible={(ctx: IFGContext<BroodRequestDto>) => {
              return ctx.formik.values.otherAddress;
            }}
            requiredMark
          />
        </FieldGroup>
        <FieldGroup columns={2}>
          <FGSelectInput
            name="postalCode"
            items={postalCodes}
            loading={pcloading}
            label={t(ETLCodes.PostalCode)}
            readonly={!editable || blocked || requestInfo?.masterRequestId > 0}
            visible={(ctx: IFGContext<BroodRequestDto>) => {
              return ctx.formik.values.otherAddress;
            }}
            requiredMark
          />
          <FGSelectInput
            name="city"
            items={localities}
            loading={locloading}
            label={t(ETLCodes.City)}
            readonly={!editable || blocked || requestInfo?.masterRequestId > 0}
            visible={(ctx: IFGContext<BroodRequestDto>) => {
              return ctx.formik.values.otherAddress;
            }}
            requiredMark
          />
        </FieldGroup>
        <FieldGroup columns={2}>
          <FGCountrySelect
            name="countryId"
            label={t(ETLCodes.Country)}
            readonly
            visible={(ctx: IFGContext<BroodRequestDto>) => {
              return ctx.formik.values.otherAddress;
            }}
            includeBelgium={true}
          />
        </FieldGroup>
        <FieldGroup columns={2}>
          <FGCustomInput label={t(ETLCodes.Affix)} visible={broodRequestId > 0}>
            {({ formik }) => (
              <Link target="_blank" to={`${ERoutes.kennel}/${data?.kennelId}/detail/view`}>
                {data?.affix}
              </Link>
            )}
          </FGCustomInput>
        </FieldGroup>
        <FieldGroup columns={2}>
          <FGDateInput
            label={t(ETLCodes.ProtrusionDate)}
            name="protrusionDate"
            readonly={
              (data?.request?.status !== ERequestState.REQUESTED &&
                data?.request?.status !== ERequestState.DOG_VALIDATION &&
                data?.request?.status !== ERequestState.GESTATION &&
                data?.request?.status !== ERequestState.BILLED) ||
              blocked ||
              requestInfo?.masterRequestId > 0
            }
            requiredMark
          />
        </FieldGroup>
        {!!broodRequestId ? (
          <FieldGroup>
            <FGSRSHCheckboxInput label={t(ETLCodes.BabyDgoPublication)} name="babyDogPublication" readonly={false} />
          </FieldGroup>
        ) : null}
      </FieldSet>

      <FieldSet title={t(ETLCodes.PotentialFathers)}>
        <FGCustomPanel>
          {({ formik }) =>
            formik?.values?.motherId ? (
              <>
                <BroodRequestPotentialFatherSelector
                  linkedIds={formik?.values?.potentialFatherIds || null}
                  onLinkedIdsChanged={(ids) => {
                    if (
                      ids.some((id) => !formik?.values?.potentialFatherIds.includes(id)) ||
                      formik?.values?.potentialFatherIds.some((id) => !ids.includes(id))
                    ) {
                      formik?.setFieldValue("potentialFatherIds", ids);
                    }
                  }}
                  motherId={formik?.values?.motherId}
                  editMode={editable || blocked}
                  loading={loading}
                  formWidth={formWidth}
                  showAllButtons={false}
                />
              </>
            ) : (
              <NonIdealState icon="resolve" description={t(ETLCodes.PleaseSelectDogToViewPossibleFathers)} />
            )
          }
        </FGCustomPanel>

        <FGCustomPanel>
          {({ formik }) =>
            requestInfo?.requestStateCode === ERequestState.LAB_WORK && (
              <FieldGroup columns={2}>
                <FGDogSearchInput
                  name="newFatherId"
                  defaultSearch={{
                    sex: EDogSex.Male,
                    filterBreeds: true,
                    dogId: data?.motherId,
                    isForBroodRequest: true,
                  }}
                  label={t(ETLCodes.NewPotentialFather)}
                  onItemViewClick={(item) => openNewTab(`${ERoutes.dog}/${item}/detail/view`)}
                  requiredMark
                />
                {data?.request?.status === ERequestState.LAB_WORK ? (
                  <Button
                    icon={IconNames.PLUS}
                    text={t(ETLCodes.AddNewPotentialFather)}
                    loading={loadingNewPotentialFather}
                    onClick={() => onAddNewFather(formik)}
                    disabled={formik?.values?.newFatherId === null || formik?.values?.newFatherId === undefined}
                  />
                ) : null}
              </FieldGroup>
            )
          }
        </FGCustomPanel>
      </FieldSet>

      {}
    </SmallFormGenerator>
  );
};
